/**
 * Segment list deduplication by canonical CIDR.
 * Matches Agent Management / SegmentsTable behavior.
 */

export const normalizeSegmentCidr = (raw) => {
  if (!raw) return raw;
  const text = String(raw);
  const direct = /^(\d+\.\d+\.\d+\.\d+\/\d+)$/.exec(text);
  if (direct) return direct[1];
  const embedded = /(\d+\.\d+\.\d+\.\d+)[_/](\d+)/.exec(text);
  if (embedded) return `${embedded[1]}/${embedded[2]}`;
  return text;
};

export const deduplicateSegments = (segments) => {
  const byCidr = new Map();
  (segments || []).forEach((seg) => {
    const key = normalizeSegmentCidr(seg.cidr || seg.segmentId);
    if (!key) return;
    if (!byCidr.has(key)) {
      byCidr.set(key, { ...seg });
      return;
    }
    const existing = byCidr.get(key);
    const isNewer = String(seg.segmentId || '').length > String(existing.segmentId || '').length;
    const preferred = isNewer ? seg : existing;
    const other = isNewer ? existing : preferred === seg ? existing : seg;
    byCidr.set(key, {
      ...preferred,
      devicesCount: Math.max(preferred.devicesCount || 0, other.devicesCount || 0),
    });
  });
  return Array.from(byCidr.values());
};

const CIDR_LABEL = /^\d+\.\d+\.\d+\.\d+\/\d+$/;

const segmentChoiceKey = (segment) => {
  const raw = String(segment?.cidr || segment?.segmentId || segment?.id || '').trim();
  const normalized = normalizeSegmentCidr(raw);
  if (CIDR_LABEL.test(normalized)) return normalized;
  return String(segment?.segmentId || segment?.id || '').trim();
};

const preferSegmentChoice = (candidate, existing) => {
  const candidatePrimary = Boolean(candidate?.primaryAgentId);
  const existingPrimary = Boolean(existing?.primaryAgentId);
  if (candidatePrimary !== existingPrimary) return candidatePrimary;
  const candidateCidr = Boolean(candidate?.cidr || candidate?.networkCidr);
  const existingCidr = Boolean(existing?.cidr || existing?.networkCidr);
  if (candidateCidr !== existingCidr) return candidateCidr;
  const candidateId = String(candidate?.segmentId || candidate?.id || '');
  const existingId = String(existing?.segmentId || existing?.id || '');
  return candidateId.length < existingId.length;
};

/**
 * One dropdown choice per subnet. Labels are the CIDR, not the stored document id.
 */
export const segmentPickerChoices = (segments) => {
  const byKey = new Map();
  (segments || []).forEach((segment) => {
    const key = segmentChoiceKey(segment);
    if (!key) return;
    const current = byKey.get(key);
    if (!current || preferSegmentChoice(segment, current)) {
      byKey.set(key, segment);
    }
  });
  return Array.from(byKey.entries()).map(([key, segment]) => {
    const value = String(segment.segmentId || segment.id || '').trim();
    const label = CIDR_LABEL.test(key) ? key : value;
    return { value, label, cidr: CIDR_LABEL.test(key) ? key : '' };
  });
};

export const segmentPickerValue = (segmentId, choices) => {
  const raw = String(segmentId || '').trim();
  if (!raw) return '';
  const options = Array.isArray(choices) ? choices : [];
  if (options.some((choice) => choice.value === raw)) return raw;
  const cidr = normalizeSegmentCidr(raw);
  const match = options.find((choice) => choice.cidr && choice.cidr === cidr);
  return match ? match.value : raw;
};

export const countDuplicateSegmentDocs = (segments) => {
  const raw = Array.isArray(segments) ? segments.length : 0;
  const unique = deduplicateSegments(segments).length;
  return Math.max(0, raw - unique);
};

export const buildTenantScopedSegmentsQuery = (tenantId, role, dealerActive = false) => {
  const params = new URLSearchParams();
  const normalized = String(role || '').toLowerCase();
  const canSendTenant = Boolean(dealerActive)
    || ['superadmin', 'admin', 'overseer', 'tenant_admin'].includes(normalized);
  if (tenantId && canSendTenant) {
    params.set('tenantId', tenantId);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};
